import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { trackWhatsAppClick } from "@/lib/analytics";
import TourCard from "@/components/TourCard";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Instagram, Quote, Star, BadgeCheck, Car, Languages, Route, MessageCircle, FileText, Check, CreditCard, UsersRound } from "lucide-react";

function TrustIcon({ index }) {
  const Icons = [BadgeCheck, Car, Languages, Route, MessageCircle];
  const Cmp = Icons[index] || BadgeCheck;
  return <Cmp size={20} />;
}

function ReassuranceIcon({ index }) {
  const Icons = [BadgeCheck, MessageCircle, Instagram, UsersRound];
  const Cmp = Icons[index] || BadgeCheck;
  return <Cmp size={20} />;
}

const HERO_IMG = "/images/oman-desert.jpeg";
const ABOUT_IMG = "/images/khalfan-about.jpeg";
const INITIAL_LOADING = {
  tours: true,
  testimonials: true,
  gallery: true,
  blog: true,
  faq: true,
  settings: true,
};

function TourCardsSkeleton() {
  return Array.from({ length: 3 }).map((_, index) => (
    <div key={index} aria-hidden="true">
      <Skeleton className="aspect-[4/5] w-full rounded-none" />
      <div className="mt-5 space-y-3">
        <Skeleton className="h-3 w-2/5" />
        <Skeleton className="h-8 w-4/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-6 w-28" />
      </div>
    </div>
  ));
}

function TestimonialsSkeleton() {
  return Array.from({ length: 3 }).map((_, index) => (
    <div key={index} aria-hidden="true" className="space-y-4">
      <Skeleton className="h-8 w-8" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-5/6" />
      <Skeleton className="h-8 w-11/12" />
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-40" />
    </div>
  ));
}

function GallerySkeleton() {
  return Array.from({ length: 6 }).map((_, index) => (
    <Skeleton
      key={index}
      aria-hidden="true"
      className={`rounded-none bg-sand-50/10 ${index % 5 === 0 ? "aspect-[4/5]" : index % 3 === 0 ? "aspect-square" : "aspect-[3/4]"}`}
    />
  ));
}

function StoriesSkeleton() {
  return Array.from({ length: 2 }).map((_, index) => (
    <div key={index} aria-hidden="true">
      <Skeleton className="aspect-[16/10] w-full rounded-none" />
      <div className="mt-5 space-y-3">
        <Skeleton className="h-8 w-4/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  ));
}

function FaqSkeleton() {
  return Array.from({ length: 4 }).map((_, index) => (
    <div key={index} aria-hidden="true" className="border-b border-charcoal/15 py-6">
      <Skeleton className="h-7 w-4/5" />
    </div>
  ));
}

export default function Home() {
  const { t, pick, lang } = useLang();
  const [tours, setTours] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [blog, setBlog] = useState([]);
  const [faq, setFaq] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(INITIAL_LOADING);

  useEffect(() => {
    let cancelled = false;

    const markLoaded = (key) => {
      if (!cancelled) setLoading((current) => ({ ...current, [key]: false }));
    };

    const loadList = async (path, setter, key) => {
      try {
        const { data } = await api.get(path);
        if (!Array.isArray(data)) throw new Error(`Invalid ${key} response`);
        if (!cancelled) setter(data);
      } finally {
        markLoaded(key);
      }
    };

    const loadSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        if (!data || typeof data !== "object" || Array.isArray(data)) {
          throw new Error("Invalid settings response");
        }
        if (!cancelled) setSettings(data);
      } finally {
        markLoaded("settings");
      }
    };

    void Promise.allSettled([
      loadList("/tours", setTours, "tours"),
      loadList("/testimonials", setTestimonials, "testimonials"),
      loadList("/gallery", setGallery, "gallery"),
      loadList("/blog", setBlog, "blog"),
      loadList("/faq", setFaq, "faq"),
      loadSettings(),
    ]);

    return () => {
      cancelled = true;
    };
  }, []);

  const whatsappLink = `https://wa.me/${(settings.whatsapp_number || "+96891291423").replace(/[^0-9]/g, "")}`;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[58svh] md:min-h-[64svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Oman desert"
            width="1024"
            height="683"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          {/* base scrim for global legibility */}
          <div className="absolute inset-0 bg-black/35" />
          {/* focused left vignette behind the text block */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-transparent" />
          {/* gentle bottom lift */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-8 md:pb-12 pt-28 w-full">
          <p className="text-xs md:text-sm uppercase tracking-[0.28em] text-sand-50/90 mb-4 fade-in-up" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>
            {t.hero.overline}
          </p>
          <h1
            className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight font-light text-sand-50 leading-[1.04] max-w-4xl fade-in-up"
            style={{ animationDelay: "120ms", textShadow: "0 2px 24px rgba(0,0,0,0.45)" }}
          >
            {t.hero.title}
          </h1>
          <p
            className="mt-5 max-w-2xl text-base md:text-xl text-sand-50 leading-relaxed fade-in-up"
            style={{ animationDelay: "240ms", textShadow: "0 1px 12px rgba(0,0,0,0.55)" }}
            data-testid="hero-lede"
          >
            {t.hero.lede}
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 fade-in-up" style={{ animationDelay: "360ms" }}>
            <Link
              to="/tours"
              data-testid="hero-tours-cta"
              className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-600 text-sand-50 rounded-full px-7 py-3 transition shadow-soft"
            >
              {t.hero.cta_primary} <ArrowRight size={18} />
            </Link>
            <a
              data-testid="hero-whatsapp-cta"
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsAppClick("home_hero")}
              className="inline-flex items-center justify-center gap-2 bg-sand-50/95 hover:bg-sand-50 text-charcoal rounded-full px-7 py-3 transition"
            >
              <MessageCircle size={18} /> {t.hero.cta_whatsapp}
            </a>
          </div>
        </div>
      </section>

      {/* HERO REASSURANCE */}
      <section className="px-6 md:px-12 py-5 md:py-7 bg-sand-50 border-b border-charcoal/10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {t.hero_reassurance.items.map((item, i) => (
            <div key={item.title} data-testid={`hero-trust-${i}`} className="flex items-start gap-3 rounded-md border border-charcoal/10 bg-white/70 px-4 py-4 pr-14 sm:pr-4">
              <div className="w-9 h-9 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center shrink-0">
                <ReassuranceIcon index={i} />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-charcoal leading-tight">{item.title}</h2>
                <p className="mt-1 text-xs text-charcoal-400 leading-relaxed">{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-b border-charcoal/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.trust.overline}</p>
              <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight font-light text-charcoal leading-[1.1]">
                {t.trust.title}
              </h2>
            </div>
            <p className="md:max-w-sm text-charcoal-400 text-base md:text-right">{t.trust.sub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-charcoal/10">
            {t.trust.points.map((p, i) => (
              <div
                key={i}
                data-testid={`trust-point-${i}`}
                className="bg-sand-50 p-7 md:p-8 hover:bg-sand-100 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center mb-5">
                  <TrustIcon index={i} />
                </div>
                <h3 className="font-serif-display text-xl md:text-2xl text-charcoal leading-tight mb-2">{p.title}</h3>
                <p className="text-sm text-charcoal-400 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOURS */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.home.tours_overline}</p>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-charcoal leading-[1.1]">
                {t.home.tours_title}
              </h2>
              <p className="mt-5 text-charcoal-400 max-w-lg">{t.home.tours_sub}</p>
            </div>
            <Link to="/tours" className="text-charcoal underline underline-offset-4 hover:text-terracotta text-sm tracking-wide" data-testid="tours-view-all">
              {t.home.view_all} →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-10" aria-busy={loading.tours}>
            {loading.tours ? (
              <TourCardsSkeleton />
            ) : (
              tours.slice(0, 3).map((tour, i) => (
                <TourCard key={tour.id} tour={tour} index={i} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* PROCESS — How booking works */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-sand-100">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.process.overline}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight font-light text-charcoal leading-[1.1]">
              {t.process.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-charcoal/10">
            {t.process.steps.map((s, i) => {
              const StepIcon = [FileText, MessageCircle, Check, CreditCard][i] || Check;
              return (
                <div key={i} data-testid={`process-step-${i}`} className="bg-sand-100 p-8 md:p-10 relative">
                  <span className="font-serif-display text-5xl text-terracotta/30 absolute top-6 right-6 leading-none">{s.n}</span>
                  <div className="w-10 h-10 rounded-full bg-terracotta text-sand-50 flex items-center justify-center mb-5">
                    <StepIcon size={18} />
                  </div>
                  <h3 className="font-serif-display text-xl md:text-2xl text-charcoal leading-tight mb-2">{s.title}</h3>
                  <p className="text-sm text-charcoal-400 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/tours" data-testid="process-cta-tours" className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-600 text-sand-50 rounded-full px-7 py-3.5 transition">
              {t.hero.cta_primary} <ArrowRight size={16} />
            </Link>
            <a data-testid="process-cta-whatsapp" href={whatsappLink} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick("home_booking_process")} className="inline-flex items-center justify-center gap-2 border border-charcoal hover:bg-charcoal hover:text-sand-50 text-charcoal rounded-full px-7 py-3.5 transition">
              {t.hero.cta_whatsapp}
            </a>
          </div>
        </div>
      </section>

      {/* ABOUT KHALFAN */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-sand-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.home.about_overline}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-charcoal leading-[1.1]">
              {t.home.about_title}
            </h2>
            {loading.settings ? (
              <div className="mt-6 min-h-24 space-y-3" aria-hidden="true">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-11/12" />
                <Skeleton className="h-5 w-4/5" />
              </div>
            ) : (
              <p className="mt-6 min-h-24 text-charcoal-400 text-base md:text-lg leading-relaxed">
                {pick(settings.about_short_en, settings.about_short_fr)}
              </p>
            )}
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-charcoal underline underline-offset-4 hover:text-terracotta" data-testid="about-more-link">
              {t.home.about_more} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <div className="aspect-[4/5] md:aspect-[5/4] overflow-hidden">
              <img
                src={ABOUT_IMG}
                alt="Khalfan"
                width="854"
                height="1280"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.home.testimonials_overline}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight font-light text-charcoal leading-[1.1]">
              {t.home.testimonials_title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16" aria-busy={loading.testimonials}>
            {loading.testimonials ? (
              <TestimonialsSkeleton />
            ) : (
              testimonials.slice(0, 3).map((t2, i) => (
                <div key={t2.id} data-testid={`testimonial-${i}`} className="relative">
                  <Quote className="text-terracotta/40 mb-4" size={32} />
                  <p className="font-serif-display text-2xl md:text-3xl text-charcoal leading-[1.3] font-light">
                    {pick(t2.content_en, t2.content_fr)}
                  </p>
                  <div className="mt-6 flex items-center gap-1 text-gold">
                    {Array.from({ length: t2.rating || 5 }).map((_, j) => (
                      <Star key={j} size={14} fill="currentColor" stroke="none" />
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-charcoal-400">
                    <span className="text-charcoal font-medium">{t2.name}</span>
                    {t2.location && ` · ${t2.location}`}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-charcoal text-sand-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.home.gallery_overline}</p>
              <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight font-light leading-[1.1]">
                {t.home.gallery_title}
              </h2>
            </div>
            <Link to="/gallery" className="text-sand-50/80 underline underline-offset-4 hover:text-terracotta text-sm">
              {t.home.see_gallery} →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" aria-busy={loading.gallery}>
            {loading.gallery ? (
              <GallerySkeleton />
            ) : (
              gallery.slice(0, 8).map((g, i) => (
                <div
                  key={g.id}
                  className={`overflow-hidden ${i % 5 === 0 ? "aspect-[4/5]" : i % 3 === 0 ? "aspect-square" : "aspect-[3/4]"}`}
                >
                  <img src={imgUrl(g.image_url)} alt={pick(g.caption_en, g.caption_fr) || "Oman"} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
              ))
            )}
          </div>
          <div className="mt-12 flex items-center gap-3 text-sm text-sand-50/70">
            <Instagram size={18} />
            <a href={`https://instagram.com/${settings.instagram_handle || "tour_guide_oman"}`} target="_blank" rel="noreferrer" className="hover:text-terracotta" data-testid="home-instagram-link">
              Follow @{settings.instagram_handle || "tour_guide_oman"} on Instagram
            </a>
          </div>
        </div>
      </section>

      {/* STORIES */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.home.stories_overline}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight font-light text-charcoal leading-[1.1]">
              {t.home.stories_title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14" aria-busy={loading.blog}>
            {loading.blog ? (
              <StoriesSkeleton />
            ) : (
              blog.slice(0, 2).map((p) => (
                <Link key={p.id} to={`/stories/${p.slug}`} className="group hover-zoom" data-testid={`story-card-${p.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-sand-200">
                    <img src={imgUrl(p.cover_image)} alt={pick(p.title_en, p.title_fr)} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-3xl text-charcoal mt-5 leading-[1.2] group-hover:text-terracotta transition">
                    {pick(p.title_en, p.title_fr)}
                  </h3>
                  <p className="mt-3 text-charcoal-400">{pick(p.excerpt_en, p.excerpt_fr)}</p>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-sand-100">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.home.faq_overline}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight font-light text-charcoal leading-[1.1] mb-12">
            {t.home.faq_title}
          </h2>
          {loading.faq ? (
            <div aria-busy="true">
              <FaqSkeleton />
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {faq.map((f) => (
                <AccordionItem key={f.id} value={f.id} className="border-charcoal/15">
                  <AccordionTrigger data-testid={`faq-trigger-${f.id}`} className="text-left font-serif-display text-xl md:text-2xl text-charcoal hover:no-underline py-6">
                    {pick(f.question_en, f.question_fr)}
                  </AccordionTrigger>
                  <AccordionContent className="text-charcoal-400 text-base leading-relaxed pb-6">
                    {pick(f.answer_en, f.answer_fr)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-terracotta text-sand-50">
        <div className={`max-w-5xl ${lang === "fr" ? "lg:max-w-6xl" : ""} mx-auto text-center md:text-left md:flex md:items-end md:justify-between gap-10`}>
          <div>
            <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight font-light leading-[1.05]">
              {t.home.cta_title}
            </h2>
            <p className="mt-5 text-sand-50/90 max-w-xl">{t.home.cta_sub}</p>
          </div>
          <div className="mt-8 md:mt-0 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={whatsappLink} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick("home_final_cta")} className="bg-sand-50 text-charcoal hover:bg-sand-200 rounded-full px-7 py-3.5 transition" data-testid="home-cta-whatsapp">
              WhatsApp
            </a>
            <Link to="/contact" className="border border-sand-50/60 hover:bg-sand-50 hover:text-charcoal rounded-full px-7 py-3.5 transition" data-testid="home-cta-contact">
              {t.contact_page.form_title}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
